/* eslint-disable react-hooks/set-state-in-effect */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  EmptyHubState,
  HiddenFileInput,
  MiniLoader,
  Pressable,
  Select,
  StatusPill,
  TextInput,
  type SelectOption,
} from '../../Common'
import { useNls } from '../../../nls/nlsProvider'
import {
  listEduFeedPosts,
  postEduFeedPost,
  uploadFeedPostFiles,
  type EduFeedPost,
} from '../../../services/eduFeedService'

export interface FeedClassOption {
  id: string
  title: string
}

export interface FeedLearner {
  learnerId: string
  displayName: string
}

export interface ClassFeedPanelProps {
  workspaceId: string
  /** nodeId of the class (used to scope posts) */
  classNodeId?: string
  /** Whether the current user is a teacher (limits class selection to their own) */
  isTeacher?: boolean
  /** Available classes (if teacher, only their classes; if admin, all workspace classes) */
  classes?: FeedClassOption[]
  /** Learners enrolled in this class (for tagging) */
  learners?: FeedLearner[]
  /** NLS kind: 'TEACHING' | 'TRAINING' */
  feedKind?: 'TEACHING' | 'TRAINING'
}

type FeedReach = 'CLASS' | 'TAGGED'

function generateRequestId(): string {
  return `feed-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

export const ClassFeedPanel: React.FC<ClassFeedPanelProps> = ({
  workspaceId,
  classNodeId,
  isTeacher = false,
  classes = [],
  learners = [],
  feedKind = 'TEACHING',
}) => {
  const { t } = useNls()

  const [posts, setPosts] = useState<EduFeedPost[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Composer state
  const [composerOpen, setComposerOpen] = useState(false)
  const [body, setBody] = useState('')
  const [reach, setReach] = useState<FeedReach>('CLASS')
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])
  const [feedFiles, setFeedFiles] = useState<File[]>([])
  const [posting, setPosting] = useState(false)
  const [postError, setPostError] = useState<string | null>(null)

  // Class selection state for composer
  const availableClassOptions: SelectOption<string>[] = useMemo(() => {
    const list: SelectOption<string>[] = []
    if (!isTeacher) {
      list.push({
        value: 'all',
        label: t('NLSEduMobileFeedAllClasses'),
      })
    }
    for (const c of classes) {
      list.push({
        value: c.id,
        label: c.title,
      })
    }
    return list
  }, [classes, isTeacher, t])

  const [selectedClassId, setSelectedClassId] = useState<string>(() => {
    if (classNodeId) return classNodeId
    if (isTeacher) return classes[0]?.id || ''
    return 'all'
  })

  useEffect(() => {
    if (classNodeId) {
      setSelectedClassId(classNodeId)
    } else if (isTeacher && classes.length > 0) {
      setSelectedClassId((prev) => (prev === 'all' || !prev ? classes[0].id : prev))
    }
  }, [classNodeId, isTeacher, classes])

  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const loadPosts = async () => {
    if (!workspaceId) return
    setLoading(true)
    setError(null)
    try {
      const res = await listEduFeedPosts({
        workspaceId,
        nodeId: classNodeId || undefined,
        pageSize: 20,
      })
      setPosts(res.posts || [])
    } catch (err: unknown) {
      setError((err as Error)?.message || 'Failed to load feed')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadPosts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, classNodeId])

  const toggleTag = (learnerId: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(learnerId) ? prev.filter((id) => id !== learnerId) : [...prev, learnerId],
    )
  }

  const handleFilesChosen = (files: FileList) => {
    const newFiles = Array.from(files)
    if (feedFiles.length + newFiles.length > 5) {
      setPostError(t('NLSEducationFeedMaxMediaError'))
      return
    }
    setFeedFiles((prev) => [...prev, ...newFiles])
  }

  const handleRemoveFile = (index: number) => {
    setFeedFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const handlePost = async () => {
    const trimmed = body.trim()
    if (!trimmed) return

    if (trimmed.length > 5000) {
      setPostError(t('NLSEducationFeedMaxCharsError'))
      return
    }

    if (reach === 'TAGGED' && selectedTagIds.length === 0) {
      setPostError(t('NLSEducationFeedReachTaggedMinOne'))
      return
    }

    if (feedFiles.length > 5) {
      setPostError(t('NLSEducationFeedMaxMediaError'))
      return
    }

    setPosting(true)
    setPostError(null)
    try {
      const requestId = generateRequestId()
      let uploadedIds: string[] = []
      if (feedFiles.length > 0) {
        uploadedIds = await uploadFeedPostFiles({
          workspaceId,
          files: feedFiles,
          requestId,
        })
      }

      const target =
        selectedClassId === 'all' && !isTeacher
          ? { allClasses: true }
          : { nodeIds: [selectedClassId || classNodeId || ''] }

      await postEduFeedPost({
        workspaceId,
        kind: feedKind,
        target,
        reach,
        body: trimmed,
        fileIds: uploadedIds,
        taggedLearnerIds: reach === 'TAGGED' ? selectedTagIds : [],
        requestId,
      })

      setBody('')
      setReach('CLASS')
      setSelectedTagIds([])
      setFeedFiles([])
      setComposerOpen(false)
      await loadPosts()
    } catch (err: unknown) {
      setPostError((err as Error)?.message || 'Failed to post')
    } finally {
      setPosting(false)
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 16px' }}>
        <MiniLoader />
      </div>
    )
  }

  return (
    <div
      className="classFeedPanel"
      style={{ padding: '12px 16px' }}
      data-testid="class-feed-panel"
    >
      {/* Composer button */}
      <div style={{ marginBottom: '14px' }}>
        <Button
          size="sm"
          variant="primary"
          onClick={() => setComposerOpen((v) => !v)}
          data-testid="feed-compose-btn"
        >
          {t('NLSEduMobileFeedCompose')}
        </Button>
      </div>

      {/* Composer form */}
      {composerOpen && (
        <div
          style={{
            marginBottom: '16px',
            padding: '12px',
            border: '1px solid var(--border-subtle)',
            borderRadius: '10px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
          data-testid="feed-composer"
        >
          {/* Class selector if classes are provided */}
          {availableClassOptions.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                {t('NLSEduMobileFeedClassSelect')}
              </span>
              <Select<string>
                value={selectedClassId}
                options={availableClassOptions}
                onChange={(val) => setSelectedClassId(String(val))}
                ariaLabel={t('NLSEduMobileFeedClassSelect')}
              />
            </div>
          )}

          <TextInput
            aria-label={t('NLSEducationFeedBodyLabel')}
            value={body}
            onChange={(val) => setBody(val)}
            placeholder={t('NLSEducationFeedBodyPlaceholder')}
            data-testid="feed-body-input"
          />

          {/* Reach toggle */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              size="sm"
              variant={reach === 'CLASS' ? 'primary' : 'secondary'}
              onClick={() => setReach('CLASS')}
              data-testid="feed-reach-class"
            >
              {t('NLSEducationFeedReachClass')}
            </Button>
            <Button
              size="sm"
              variant={reach === 'TAGGED' ? 'primary' : 'secondary'}
              onClick={() => setReach('TAGGED')}
              data-testid="feed-reach-tagged"
            >
              {t('NLSEducationFeedReachTagged')}
            </Button>
          </div>

          {/* Learner tags (shown when TAGGED reach) */}
          {reach === 'TAGGED' && learners.length > 0 && (
            <div
              style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}
              data-testid="feed-learner-tags"
            >
              {learners.map((learner) => {
                const selected = selectedTagIds.includes(learner.learnerId)
                return (
                  <Pressable
                    key={learner.learnerId}
                    onClick={() => toggleTag(learner.learnerId)}
                    data-testid={`feed-tag-learner-${learner.learnerId}`}
                    style={{
                      padding: '4px 10px',
                      borderRadius: '20px',
                      border: '1px solid var(--border-subtle)',
                      backgroundColor: selected
                        ? 'var(--primary)'
                        : 'var(--surface-card)',
                      color: selected ? 'var(--text-strong)' : 'inherit',
                      fontSize: '13px',
                    }}
                  >
                    {learner.displayName}
                  </Pressable>
                )
              })}
            </div>
          )}

          {/* File attachments */}
          <div>
            <HiddenFileInput
              ref={fileInputRef}
              multiple
              accept="image/*,video/*"
              onFiles={handleFilesChosen}
              testId="feed-file-input"
            />
            <Button
              size="sm"
              variant="secondary"
              onClick={() => fileInputRef.current?.click()}
              data-testid="feed-attach-btn"
            >
              {t('NLSEducationFeedMediaLabel')}
            </Button>

            {feedFiles.length > 0 && (
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '6px' }}
                data-testid="feed-attached-files"
              >
                {feedFiles.map((f, i) => (
                  <div
                    key={`${f.name}-${i}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontSize: '12px',
                    }}
                  >
                    <span>{f.name}</span>
                    <Pressable
                      onClick={() => handleRemoveFile(i)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--danger)',
                      }}
                      data-testid={`feed-remove-file-${i}`}
                    >
                      {t('NLSEduMobileFeedRemoveFile')}
                    </Pressable>
                  </div>
                ))}
              </div>
            )}
          </div>

          {postError && (
            <div
              style={{ color: 'var(--danger)', fontSize: '12px' }}
              data-testid="feed-post-error"
            >
              {postError}
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setComposerOpen(false)}
              data-testid="feed-cancel-btn"
            >
              {t('NLSEducationFeedCancelButton')}
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={posting || !body.trim()}
              onClick={handlePost}
              data-testid="feed-post-btn"
            >
              {posting ? t('NLSEduMobileFeedPosting') : t('NLSEduMobileFeedPostBtn')}
            </Button>
          </div>
        </div>
      )}

      {error && (
        <div style={{ color: 'var(--danger)', marginBottom: '12px', fontSize: '13px' }}>
          {error}
        </div>
      )}

      {posts.length === 0 ? (
        <EmptyHubState
          title={t('NLSEduMobileFeedEmptyTitle')}
          description={t('NLSEduMobileFeedEmptyBody')}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }} data-testid="feed-list">
          {posts.map((post) => (
            <div
              key={post.id}
              style={{
                padding: '12px',
                borderRadius: '10px',
                border: '1px solid var(--border-subtle)',
                backgroundColor: 'var(--surface-card)',
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
              }}
              data-testid={`feed-post-${post.id}`}
            >
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                {post.createdByName}
              </div>
              <div style={{ fontSize: '14px' }}>{post.body}</div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <StatusPill tone="neutral" compact>
                  {t('NLSEduMobileFeedReach', { count: String(post.seenCount ?? 0) })}
                </StatusPill>
                {post.reach === 'TAGGED' && (
                  <StatusPill tone="info" compact>
                    {t('NLSEducationFeedReachTagged')}
                  </StatusPill>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
